'use client';

// Drives the modal-heatmap, volumetric pressure, and raytrace
// visualisations. Modal re-fetches on frequency / view / pipeline /
// scene change (debounced); the volumetric field and raytrace each
// keep a Before slot (no panels) and an After slot (doc.panels) so
// the result-step Before/After toggle is a state flip, not a refetch
// loop. The "other" slot is fetched eagerly in the background once
// the active one resolves, so flipping the toggle is instant after
// the first paint.
//
// Modal heat-map (`vizMode='modal'`) stays single-slot — it's an
// advanced-editor surface picker, not the result-page summary, and
// the before/after framing doesn't apply there.
//
// A Design's viz pairs (modal/raytrace/volume) can come from FOUR places,
// in the priority order the design-switch effect below checks them. Three
// real bugs were found and fixed across two sessions (2026-08-21,
// 2026-08-22) inside that one effect, each because this map had to be
// re-derived by hand from ~450 lines rather than read off a comment — if
// you're debugging a stale/blank 3D view, start here before reading
// further:
//   1. `vizDesignCache` (this file's own `vizCacheRef`) — pairs this hook
//      already fetched/computed for a Design THIS browser session, stashed
//      when switching away from it. Cheapest, most common hit after the
//      first visit to a tab.
//   2. Fork-inherit — a brand-new local fork starts as an exact copy of its
//      parent's current pairs (see the design-switch effect's own comment
//      on the `activeItem?.parentId === leavingId` branch). Only applies
//      the instant a fork is created, before it's ever been independently
//      re-Computed.
//   3. `persistedVizByDesign` (from `useRoomSession()`, ultimately
//      `iterationsFromDesigns.ts`'s `persistedVizByDesignId`) — what the
//      BACKEND actually has saved for this Design, keyed by id. This is
//      the only source that covers "switch to a sibling Design that was
//      never visited this session but WAS already simulated" — added
//      2026-08-22 after this exact gap silently dropped every sibling's
//      saved viz_json on the floor.
//   4. Live fetch (the volumetric/raytrace/modal effects further down) —
//      the fallback of last resort. Raytrace has no voxel gate. Volumetric
//      / velocity are voxel-armed-gated in the advanced editor so a sofa
//      drag cannot kick an eigensolve — except the placement gallery
//      (`liveViz` + advanced), which must paint without Compute.
// A Design's persisted viz can ALSO come from `persistedViz` (singular,
// no "ByDesign") — the one payload seeded into this hook's `useState`
// initializers below at HOOK MOUNT, for whichever Design happened to be
// active on page load. That's really the same data as source 3 above for
// exactly one Design; kept separate because it needs to run before the
// design-switch effect has ever fired.

import { useEffect, useRef } from 'react';
import { EVENTS, track } from '@/lib/analytics';
import { adminApi } from '@/lib/adminApi';
import { useEditor, useDispatch } from '../state/EditorContext';
import { docToScenePayload, docToRaytraceRequest } from '../state/serialize';
import { geometrySignature, preWarmEigensolve } from '@/lib/preWarmEigensolve';
import {
  volumetricSig, getCachedVolumetric, fetchVolumetric, primeCachedVolumetric, VOL_SWEEP,
} from '@/lib/volumetricCache';
import {
  raytraceSig, getCachedRaytrace, fetchRaytrace, primeCachedRaytrace,
} from '@/lib/raytraceCache';
import {
  modalSig, getCachedModal, fetchModal, primeCachedModal,
} from '@/lib/modalCache';
import {
  VELOCITY_BAND,
  velocitySig,
  getCachedVelocity,
  fetchVelocity,
  primeCachedVelocity,
} from '@/lib/velocityCache';
import { inlineVolumeResponse } from '@/lib/resolveVolumeBytes';
import { resolveModal, resolveRaytrace } from '@roomtreatment/api-client';
import type {
  ModalResponse, ModalRefResponse, RaytraceResponse, RaytraceRefResponse,
  ModalVolumeSweepResponse, ModalVelocityResponse,
  VolumeSweepRefResponse, VelocityRefResponse,
  SurfaceKey,
} from '@/lib/adminTypes';
import type { PersistedVizPayload, PersistedVizPayloadV1, PersistedVizPair } from '@roomtreatment/core';
import {
  analysisMatchesScene, buildPersistedVizPayloadV2, vizFromPersisted, isModalRef, isRaytraceRef,
} from '@roomtreatment/core';
import { createVizDesignCache } from './vizDesignCache';
import { shouldHoldVizFetch } from './vizFetchGate';
import { VIZ_STATUS, vizFailureCode, type VizFailure } from './vizStatus';
import { useVizSnapshot } from '../state/VizSnapshotContext';
import { useIdempotentState } from '@/lib/useIdempotentState';

/** `analysis.viz`'s four layers are EITHER inline (a legacy v1-hydrated
 *  room, or a bare-room "before" probe this hook already fetched live) or
 *  an artifact reference (a fresh `analyze_scene`, job pipeline W2 for
 *  `volume`/`velocity`, W5 for `modal`/`raytrace`). Decode either into the
 *  legacy inline shape — `resolveVolumeBytes` + `bytesToB64` for volume/
 *  velocity, `resolveModal`/`resolveRaytrace` (`@roomtreatment/api-client`)
 *  for modal/raytrace — so this hook's existing per-layer caches and every
 *  downstream 3D consumer (`packVolumetricCloud`, `ModalHeatmap.tsx`,
 *  `RaytraceCloud.tsx`, ...) keep working completely unchanged — a later
 *  pass can switch the volume/velocity consumers to the `*Async` variants
 *  (`packVolumetricCloudAsync`/`packVolumetricVelocityAsync`) to drop the
 *  extra base64 round trip this costs. */
async function toInlineVolume(
  v: ModalVolumeSweepResponse | VolumeSweepRefResponse,
): Promise<ModalVolumeSweepResponse> {
  return inlineVolumeResponse<ModalVolumeSweepResponse, VolumeSweepRefResponse>(
    v, adminApi.fetchArtifact,
  );
}

async function toInlineVelocity(
  v: ModalVelocityResponse | VelocityRefResponse,
): Promise<ModalVelocityResponse> {
  return inlineVolumeResponse<ModalVelocityResponse, VelocityRefResponse>(
    v, adminApi.fetchArtifact,
  );
}

async function toInlineModal(v: ModalResponse | ModalRefResponse): Promise<ModalResponse> {
  return resolveModal(v, adminApi.fetchArtifact);
}

async function toInlineRaytrace(v: RaytraceResponse | RaytraceRefResponse): Promise<RaytraceResponse> {
  return resolveRaytrace(v, adminApi.fetchArtifact);
}

type Pair<T> = { before: T | null; after: T | null };

const VIZ_FAILED_EVENT = (EVENTS as Record<string, string>).viz_failed ?? 'viz_failed';

function vizFailedPipeline(kind: VizFailure): 'modal' | 'volume' | 'raytrace' | 'share' {
  return kind === 'velocity' ? 'volume' : kind;
}

function trackVizFailed(kind: VizFailure) {
  track(VIZ_FAILED_EVENT, {
    pipeline: vizFailedPipeline(kind),
    error_code: vizFailureCode(kind),
  });
}

/** Pick the active before/after slot, falling back when only one side was
 *  persisted (typical on public share when treatment wasn't compared). */
function activePairSlot<T>(pair: Pair<T>, compare: 'before' | 'after'): T | null {
  return pair[compare] ?? pair.after ?? pair.before ?? null;
}

export interface UseModalVizOptions {
  readOnly?: boolean;
  /** Precomputed data or nothing — never reach the solver. See
   *  `shouldHoldVizFetch`; the showcase needs it because its bundles carry
   *  only the one layer each room paints. */
  neverFetch?: boolean;
  persistedViz?: PersistedVizPayload | null;
  /** Every Design's own persisted viz, keyed by (string) id — owner/editor
   *  sessions only. Lets the design-switch effect below seed a tab that's
   *  never been visited this session, instead of leaving it blank behind a
   *  fetch that may be voxel-gated (see that effect's own comment). */
  persistedVizByDesign?: Record<string, PersistedVizPayload>;
  /** Override the chrome-mode live-paint rule. Guided is live; advanced
   *  holds until Compute except the placement gallery (stepViewportHints
   *  `liveViz`), whose three layouts are already solved. */
  liveViz?: boolean;
}

export function useModalViz(mode: 'guided' | 'advanced', opts?: UseModalVizOptions) {
  const readOnly = opts?.readOnly ?? false;
  const neverFetch = opts?.neverFetch ?? false;
  const persisted = opts?.persistedViz ?? null;
  const persistedByDesign = opts?.persistedVizByDesign;
  const state = useEditor();
  const dispatch = useDispatch();
  // `modalFreq` no longer drives the volumetric fetch — the sweep cube
  // covers the whole 20-200 Hz band in one call.  Modal heatmap still
  // re-fetches per-frequency (single-surface render, no need to preload).
  const { vizMode, modalFreq, modalView, compare } = state.view;
  const pipeline = state.settings.pipeline;
  const voxelArmedSig = state.analysis.voxelArmedSig;
  const token = state.analysis.token;
  // Guided paints live: generate-step candidate SYNC_TRANSFORM stales
  // analysis on purpose, but the gallery still needs the volumetric field,
  // so viz keys off `token` and the paint/fetch hold is skipped. Advanced
  // holds until Compute, and uses analysisMatchesScene so one undo of N
  // edits cannot resurrect the last scan — except when `liveViz` is set
  // (placement gallery), which is the same branch as guided generate.
  const live = opts?.liveViz ?? (mode !== 'advanced');
  const vizTrigger = live ? token : state.analysis.scanNonce;
  const vizGateOk = live || analysisMatchesScene(state.analysis);
  const beforeStaleAtMount = state.analysis.beforeStale;
  const afterStaleAtMount = state.analysis.afterStale;

  const docRef = useRef(state.doc);
  const armedSigRef = useRef(voxelArmedSig);
  const analysisVizRef = useRef(state.analysis.viz);
  useEffect(() => {
    docRef.current = state.doc;
    armedSigRef.current = voxelArmedSig;
    analysisVizRef.current = state.analysis.viz;
  });

  // Voxel gate (shared with useAutoSim): voxel consumers MUST NOT trigger
  // an eigensolve.  The eigensolve only fires from the furniture step's
  // "Set up speakers" click (useStepFlow.tsx's armVoxelIfChanged).  Returns
  // true only when the armed signature matches the current geometry; false
  // means "voxel not available for this geometry, no-op this consumer."
  const voxelArmedForCurrent = (): boolean => {
    return armedSigRef.current === geometrySignature(docRef.current);
  };

  // Guided stays gated (wizard arms voxel on furniture Continue). Advanced
  // stays gated too — except the generate gallery, the only advanced path
  // with liveViz. That gallery must fetch/paint without Compute.
  const skipVoxelGate = live && mode === 'advanced';
  // Job pipeline W2 (2026-09-06): a scene that still matches its last real
  // Compute (`analysisMatchesScene`) is provably safe to lazily eigensolve
  // for — that Compute already ran one for this exact geometry — so it is
  // NOT gated on `voxelArmedSig` too. Without this, a layer `analyze_scene`
  // legitimately left missing (a legacy v1 room's `viz_json` never captured
  // it; the user switches to a viz mode nothing has fetched yet this
  // session) could never lazily load on a fresh page load: `voxelArmedSig`
  // only gets set by an in-session ARM_VOXEL (ScanButton / the furniture
  // step), which nothing runs automatically on reload. This is the fix for
  // the diagnosed "reload shows 'Simulation pending' forever for an
  // unopened viz mode even though the scene never changed" bug.
  const voxelFetchBlocked = (): boolean =>
    !skipVoxelGate && !readOnly && pipeline === 'voxel'
    && !voxelArmedForCurrent() && !analysisMatchesScene(state.analysis);

  // Seed from the persisted payload on mount — for a public-share (readOnly)
  // session unconditionally (there's no live Compute there, so whatever was
  // saved is all there ever will be), and for the OWNER'S OWN session too as
  // long as the relevant side isn't stale. This is the fix for "3D view
  // loses its color after My Rooms reopen": the owner path used to require a
  // fresh in-session Scan before showing anything, even though the correct
  // cube was already sitting in `persisted`. A stale side is deliberately
  // left null here — it'll either come from the next Compute, or (for
  // read-only) just wasn't computed for this share.
  const seedSideFor = <T,>(
    pair: PersistedVizPair<Record<string, unknown>> | undefined,
    side: 'before' | 'after',
    beforeStale: boolean,
    afterStale: boolean,
  ): T | null => {
    const raw = pair?.[side];
    if (raw == null) return null;
    if (!readOnly && (side === 'before' ? beforeStale : afterStale)) return null;
    return raw as unknown as T;
  };
  const seedSide = <T,>(pair: PersistedVizPair<Record<string, unknown>> | undefined, side: 'before' | 'after'): T | null =>
    seedSideFor<T>(pair, side, beforeStaleAtMount, afterStaleAtMount);
  // `before` (the bare-room comparison) is ONLY ever persisted in a v1
  // payload — see `PersistedVizPayloadV2`'s header — so narrow to v1 before
  // reading any `.before` side. v2's `.after`-equivalent goes through
  // `effectiveAfterViz` below instead of this at all.
  const persistedV1: PersistedVizPayloadV1 | undefined =
    persisted?.version === 1 ? persisted : undefined;

  // Job pipeline W2 (2026-09-06): the "after" (current-scene) slot for all
  // four layers no longer comes from `persistedViz`/`persistedVizByDesign`
  // — it comes from `analysis.viz`, which already round-trips per-Design
  // through the reducer (fork/switch/undo, `analysisFromResults` on
  // hydrate) exactly like `analysis.freq`/`rt60` do. A read-only public
  // share has no live `analysis.viz` (no Compute ever ran in that session),
  // so it keeps reading `persistedViz` — `vizFromPersisted` normalizes
  // either wire version into the same single-valued shape `analysis.viz`
  // already has, so this hook needs exactly one downstream code path for
  // "after" regardless of which source it came from.
  const afterVizUsable = readOnly || analysisMatchesScene(state.analysis);
  const effectiveAfterViz = !afterVizUsable
    ? null
    : readOnly
      ? vizFromPersisted(persisted)
      : state.analysis.viz;

  /** Synchronous seed only — an artifact-referenced layer resolves a moment
   *  later via the priming effect below (network fetch cannot be
   *  synchronous), so a ref-shaped value must NOT be exposed to consumers
   *  here: they index `field_b64`/`positions`/`surfaces[key].grid` as plain
   *  arrays and would read a hole (`ArtifactRef` has none of those shapes)
   *  until the async effect corrects it. An already-inline value (legacy
   *  v1 data, or a bare-room probe this hook fetched live earlier off the
   *  standalone endpoints) still seeds immediately here. */
  function seedAfterInline<T extends { field_b64?: unknown }>(
    value: T | { field: unknown; inside: unknown } | null | undefined,
  ): T | null {
    if (value == null) return null;
    if ('field_b64' in value) return value as T;
    return null;
  }

  function seedAfterModal(value: ModalResponse | ModalRefResponse | null | undefined): ModalResponse | null {
    if (value == null || isModalRef(value)) return null;
    return value;
  }

  function seedAfterRaytrace(
    value: RaytraceResponse | RaytraceRefResponse | null | undefined,
  ): RaytraceResponse | null {
    if (value == null || isRaytraceRef(value)) return null;
    return value;
  }

  // `useIdempotentState`, not `useState`. Several effects below re-seed
  // these pairs on every `vizTrigger` bump — i.e. once per scene edit, i.e.
  // once per pointermove of any drag — and deliberately resolve to the SAME
  // reference when the caches hold nothing newer. Plain `useState` schedules
  // a render for that no-op anyway whenever the component already has
  // pending work (see useIdempotentState), which turns a resync that was
  // designed to be free into a guaranteed second render per pointermove.
  const [modalPair, setModalPair] = useIdempotentState<Pair<ModalResponse>>(() => ({
    before: seedSide<ModalResponse>(persistedV1?.modal, 'before'),
    after: seedAfterModal(effectiveAfterViz?.modal as ModalResponse | ModalRefResponse | null | undefined),
  }));
  const [raytracePair, setRaytracePair] = useIdempotentState<Pair<RaytraceResponse>>(() => ({
    before: seedSide<RaytraceResponse>(persistedV1?.raytrace, 'before'),
    after: seedAfterRaytrace(
      effectiveAfterViz?.raytrace as RaytraceResponse | RaytraceRefResponse | null | undefined,
    ),
  }));
  const [volumePair, setVolumePair] = useIdempotentState<Pair<ModalVolumeSweepResponse>>(() => ({
    before: seedSide<ModalVolumeSweepResponse>(persistedV1?.volume, 'before'),
    after: seedAfterInline<ModalVolumeSweepResponse>(
      effectiveAfterViz?.volume as ModalVolumeSweepResponse | { field: unknown; inside: unknown } | null,
    ),
  }));

  const [velocityPair, setVelocityPair] = useIdempotentState<Pair<ModalVelocityResponse>>(() => ({
    before: seedSide<ModalVelocityResponse>(persistedV1?.velocity, 'before'),
    after: seedAfterInline<ModalVelocityResponse>(
      effectiveAfterViz?.velocity as ModalVelocityResponse | { field: unknown; inside: unknown } | null,
    ),
  }));

  const activeId = state.iterations.activeId;
  const vizCacheRef = useRef(createVizDesignCache());
  const prevActiveIdRef = useRef(activeId);
  const skipDesignCacheMountRef = useRef(true);
  const modalPairRef = useRef(modalPair);
  const raytracePairRef = useRef(raytracePair);
  const volumePairRef = useRef(volumePair);
  const velocityPairRef = useRef(velocityPair);
  useEffect(() => {
    modalPairRef.current = modalPair;
    raytracePairRef.current = raytracePair;
    volumePairRef.current = volumePair;
    velocityPairRef.current = velocityPair;
  });

  const { register, markVizChanged } = useVizSnapshot();
  const vizGateOkRef = useRef(vizGateOk);
  vizGateOkRef.current = vizGateOk;
  useEffect(() => {
    if (readOnly) {
      register(null);
      return () => register(null);
    }
    register(() => {
      // Job pipeline W2 (2026-09-06)/W5: persist FROM `analysis.viz` (the
      // reducer's own per-Design state, already the source of truth for
      // "after"), never from this hook's local pairs — those hold the
      // INLINE-decoded form (`toInlineVolume`/`toInlineVelocity`/
      // `toInlineModal`/`toInlineRaytrace`) so the existing per-layer
      // caches and 3D consumers keep working unchanged, which is exactly
      // the bulk shape `viz_json` must NOT carry any more. A layer whose
      // `analysis.viz` value is still the legacy inline shape (an
      // un-recomputed v1-hydrated room, or a bare-room "before" probe this
      // hook fetched live off the standalone endpoints) is deliberately
      // OMITTED rather than persisted — see `PersistedVizPayloadV2`'s
      // header; it comes back as "missing, recompute on next Compute"
      // instead of writing a multi-hundred-KB cube/array back into v2 (the
      // exact prod cost `raytrace_refs`/`modal_surface_refs` exist to cut —
      // see backend/routers/admin_physics.py).
      const viz = analysisVizRef.current;
      if (!viz) return null;
      const isVolumeLikeRef = (v: unknown): boolean =>
        v != null && typeof v === 'object' && 'field' in v && 'inside' in v && !('field_b64' in v);
      return buildPersistedVizPayloadV2({
        modal: isModalRef(viz.modal) ? (viz.modal as unknown as Record<string, unknown>) : null,
        raytrace: isRaytraceRef(viz.raytrace) ? (viz.raytrace as unknown as Record<string, unknown>) : null,
        volume: isVolumeLikeRef(viz.volume) ? (viz.volume as unknown as Record<string, unknown>) : null,
        velocity: isVolumeLikeRef(viz.velocity) ? (viz.velocity as unknown as Record<string, unknown>) : null,
      });
    });
    return () => register(null);
  }, [readOnly, register]);

  // The volumetric/raytrace/modal fields land asynchronously, well after the
  // treatment-completion auto-save already fired (see useAutoSave.ts) — that
  // save runs the instant freq/rt60 analysis is ready, which is BEFORE these
  // network fetches resolve. Without this, the just-computed viz cube only
  // reaches the server on whatever the user clicks next (typically Share),
  // which is what showed up as a second, seemingly-redundant "Saving…" right
  // after Share. Tell useAutoSave a fresher cube exists so it can persist it
  // in the background instead, so a subsequent Share/Save has nothing left
  // to do.
  const vizMountedRef = useRef(false);
  useEffect(() => {
    if (readOnly) return;
    if (!vizMountedRef.current) {
      vizMountedRef.current = true;
      return;
    }
    markVizChanged();
    // Depend on the PAYLOAD references (modalPair.before/.after, etc.), not
    // the wrapper objects. Several producers in this file (the resync effect
    // below in particular) rebuild `{ before, after }` as a fresh object on
    // every firing even when neither slot's actual value changed — a
    // `prev => ({...})` pattern with no reference-preserving bail-out.
    // Depending on the wrapper turned that churn into a real bug: this
    // effect fired -> markVizChanged() -> vizVersion bumped -> re-render ->
    // some effect elsewhere rebuilds a pair wrapper again -> this effect
    // fires again -> ... a self-sustaining loop that tripped React's
    // "Maximum update depth exceeded" safety net and, worse, starved the
    // Compute click's own state update of a chance to land (confirmed live:
    // the loop and "Compute never leaves the stale/loading state" always
    // reproduced together). The payload values themselves only change when
    // a fetch actually lands new data or a real design switch reseeds them,
    // so keying on them here is both correct (this notifies "there's a
    // fresher viz cube to persist," which is exactly whether the payload
    // changed) and immune to the wrapper's referential churn.
  }, [
    modalPair.before, modalPair.after,
    raytracePair.before, raytracePair.after,
    volumePair.before, volumePair.after,
    velocityPair.before, velocityPair.after,
    readOnly, markVizChanged,
  ]);

  useEffect(() => {
    if (skipDesignCacheMountRef.current) {
      skipDesignCacheMountRef.current = false;
      prevActiveIdRef.current = activeId;
      return;
    }
    if (prevActiveIdRef.current === activeId) return;

    const leavingId = prevActiveIdRef.current;
    prevActiveIdRef.current = activeId;

    // REBIND_ITERATION (a fork's create() round-tripping, or the room's own
    // first save) relabels the CURRENTLY active Design from a local id to
    // its real backend id — activeId changes, but what's actually active
    // does not. `leavingId` (the pre-rebind id) is then completely absent
    // from `items`, since REBIND replaces it in place rather than leaving
    // it behind as a sibling. A genuine switch to a different Design always
    // leaves the id being switched away from still present in `items`. When
    // `leavingId` is gone entirely, the viz pairs already sitting in local
    // state are still correct for the (same, just relabeled) Design — keep
    // them as-is instead of falling into the switch logic below, which
    // otherwise treats this as "switched to some uncached Design" and, since
    // `activeItem.parentId` can never equal a `leavingId` that no longer
    // corresponds to anything, ends up nulling out the pairs it had just
    // correctly loaded a moment earlier. This was a real, reproducible bug:
    // a fresh fork's 3D view (or a room's own first-save-after-Compute view)
    // went blank right after the rebind, and only came back once an
    // unrelated state change (e.g. toggling viz mode away and back) forced
    // a re-render for a different reason.
    if (!state.iterations.items.some((it) => it.id === leavingId)) {
      return;
    }

    vizCacheRef.current.stash(leavingId, {
      modal: modalPairRef.current,
      raytrace: raytracePairRef.current,
      volume: volumePairRef.current,
      velocity: velocityPairRef.current,
    });

    const restored = vizCacheRef.current.take(activeId);
    if (restored) {
      setModalPair(restored.modal as Pair<ModalResponse>);
      setRaytracePair(restored.raytrace as Pair<RaytraceResponse>);
      setVolumePair(restored.volume as Pair<ModalVolumeSweepResponse>);
      setVelocityPair(restored.velocity as Pair<ModalVelocityResponse> ?? { before: null, after: null });
      return;
    }
    // A brand-new local fork (FORK_ITERATION / "Duplicate design") starts as
    // an exact geometric copy of its parent — its 3D viz cube is therefore
    // still correct, not stale, the instant the fork is created. Without
    // this, switching straight to a just-forked tab always missed the
    // per-design cache (a fork's id has never been cached) and blanked the
    // 3D view, even though "Duplicate design" is explicitly supposed to hand
    // back an already-simulated room — Graphs/freq/rt60 copy correctly
    // (they live on `analysis`, cloned straight into the new DesignRecord by
    // the FORK_ITERATION reducer case), but this viz-pair cache is separate
    // state this hook owns, keyed by design id, and forking mints a brand
    // new id. `modalPairRef`/`raytracePairRef`/`volumePairRef` above still
    // hold the LEAVING design's pairs at this exact point (they were just
    // stashed, but refs update synchronously, before any re-render) — safe
    // to reuse directly for the fork.
    const activeItem = state.iterations.items.find((it) => it.id === activeId);
    if (activeItem?.parentId === leavingId) {
      // Shallow-clone, don't hand back the SAME object references: React
      // bails out of re-rendering (and any consumer's `useEffect` keyed on
      // one of these pairs skips too) when a `setState` call receives the
      // exact reference the state already holds. The 3D canvas never
      // unmounts on a Design switch (RoomShell keeps one persistent
      // SceneCanvas), so whatever component uploads this data to the GPU
      // relies on that reference actually changing to know new data
      // arrived — passing the untouched ref left the fork's 3D view
      // painted blank until an unrelated state change (e.g. toggling viz
      // mode away and back) forced a re-render for a different reason.
      setModalPair({ ...modalPairRef.current });
      setRaytracePair({ ...raytracePairRef.current });
      setVolumePair({ ...volumePairRef.current });
      setVelocityPair({ ...velocityPairRef.current });
      return;
    }
    // Switching to a Design that's never been visited this session (an
    // existing sibling tab, not a just-created fork) has nothing in
    // `vizCacheRef` either. `before` (the bare-room comparison) is only
    // ever available from a v1-persisted payload keyed by Design id — v2
    // never carries one (see `PersistedVizPayloadV2`'s header) — so seed it
    // from there when present. `after` needs no seeding here at all: this
    // Design's `analysis.viz` was ALREADY switched in by the very
    // `SWITCH_ITERATION`/`activateDesign` dispatch that changed `activeId`
    // (it round-trips through the reducer exactly like `analysis.freq`
    // does — see `cloneAnalysis`/`analysisFromResults` in editorState.ts),
    // and the priming effect further below reads it directly.
    const persistedForActiveV1 = persistedByDesign?.[activeId];
    const persistedForActive = persistedForActiveV1?.version === 1 ? persistedForActiveV1 : undefined;
    if (persistedForActive && activeItem) {
      const beforeStale = activeItem.analysis.beforeStale;
      const afterStale = activeItem.analysis.afterStale;
      setModalPair((prev) => ({
        before: seedSideFor<ModalResponse>(persistedForActive.modal, 'before', beforeStale, afterStale),
        after: prev.after,
      }));
      setRaytracePair((prev) => ({
        before: seedSideFor<RaytraceResponse>(persistedForActive.raytrace, 'before', beforeStale, afterStale),
        after: prev.after,
      }));
      setVolumePair((prev) => ({
        before: seedSideFor<ModalVolumeSweepResponse>(persistedForActive.volume, 'before', beforeStale, afterStale),
        after: prev.after,
      }));
      setVelocityPair((prev) => ({
        before: seedSideFor<ModalVelocityResponse>(persistedForActive.velocity, 'before', beforeStale, afterStale),
        after: prev.after,
      }));
      return;
    }
    setModalPair({ before: null, after: null });
    setRaytracePair({ before: null, after: null });
    setVolumePair({ before: null, after: null });
    setVelocityPair({ before: null, after: null });
    // seedSideFor intentionally elided — a fresh function reference every
    // render (same reasoning as buildModalScenePayload below); including it
    // would retrigger this effect on every render instead of only on a real
    // Design switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, state.iterations.items, persistedByDesign]);

  // Job pipeline W2 (2026-09-06): prime the "after" (current-scene) slot for
  // all four layers from `analysis.viz` — the bundle `analyze_scene`
  // returns alongside freq/rt60/soundstage, restored from `viz_json` on
  // hydrate exactly like those are. This is what makes reload / a Compute
  // that never happened to open a given viz mode / switching to a sibling
  // Design all paint immediately instead of falling into the live-fetch
  // effects below (kept as the fallback for the Before slot, which
  // `analyze_scene` does not cover — see this hook's own header comment —
  // and for a legacy room whose v1 `viz_json` never captured a layer).
  // PRIMES the module-level caches at the same signature the live-fetch
  // effects key on, so those effects' own cache-hit checks make this a true
  // loader: once primed, they never call the solver for a layer this
  // effect already has.
  useEffect(() => {
    if (readOnly || neverFetch) return;
    if (!effectiveAfterViz) return;
    let cancelled = false;
    (async () => {
      if (effectiveAfterViz.modal) {
        const modal = await toInlineModal(
          effectiveAfterViz.modal as ModalResponse | ModalRefResponse,
        );
        if (cancelled) return;
        const sig = modalSigForSide('after');
        primeCachedModal(sig, modal);
        setModalPair((prev) => (prev.after === modal ? prev : { ...prev, after: modal }));
      }
      if (effectiveAfterViz.raytrace) {
        const raytrace = await toInlineRaytrace(
          effectiveAfterViz.raytrace as RaytraceResponse | RaytraceRefResponse,
        );
        if (cancelled) return;
        const sig = raytraceSigForSide('after');
        primeCachedRaytrace(sig, raytrace);
        setRaytracePair((prev) => (prev.after === raytrace ? prev : { ...prev, after: raytrace }));
      }
      if (effectiveAfterViz.volume) {
        const inline = await toInlineVolume(
          effectiveAfterViz.volume as ModalVolumeSweepResponse | VolumeSweepRefResponse,
        );
        if (cancelled) return;
        const sig = volumetricSigForSide('after');
        primeCachedVolumetric(sig, inline);
        setVolumePair((prev) => (prev.after === inline ? prev : { ...prev, after: inline }));
      }
      if (effectiveAfterViz.velocity) {
        const inline = await toInlineVelocity(
          effectiveAfterViz.velocity as ModalVelocityResponse | VelocityRefResponse,
        );
        if (cancelled) return;
        const sig = velocitySigForSide('after');
        primeCachedVelocity(sig, inline);
        setVelocityPair((prev) => (prev.after === inline ? prev : { ...prev, after: inline }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // modalSigForSide/raytraceSigForSide/volumetricSigForSide/velocitySigForSide
    // intentionally elided — fresh function references every render (same
    // reasoning as every other effect in this file); including them would
    // retrigger this effect on every render instead of only when a fresh
    // `analysis.viz` actually lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAfterViz, readOnly, neverFetch]);

  // Re-seed the before/after slots whenever the scene changes (same trigger
  // that bumps `token`).  BOTH raytrace and volumetric re-seed from their
  // module-level caches: volumetric keys on the FIELD-determining signature
  // (geometry + sources + materials + panels, NOT listener); raytrace keys on
  // the full request (listener INCLUDED — the ray cloud's energies are sampled
  // at the seat).  So flipping between candidates already browsed is an
  // instant state flip with no recompute — switching placements in the editor
  // pulls each room's cached trace/field instead of re-solving.  A cold scene
  // seeds null and the fetch effects below fill it.
  //
  // MERGE, don't replace: `vizTrigger` bumps for two different reasons that
  // both land here — a genuine scene edit (a real cache miss, where nulling
  // the stale slot until the fetch effect below repopulates it is correct),
  // and a chrome-mode flip alone (guided -> advanced graduation swaps
  // `vizTrigger`'s SOURCE from `token` to `scanNonce`, a discontinuous value
  // change with NO scene edit behind it). The second case used to null out
  // an already-correct, already-painted field for the ~200ms-plus the fetch
  // effect takes to re-resolve it from the SAME cache entry — a real,
  // reproducible "3D view goes blank right after Results / after entering
  // the editor" bug, since nothing forces a repaint once the fetch resolves
  // if the user doesn't happen to interact again. Keeping the previous value
  // when the cache doesn't (yet) have a fresher one means a genuine edit
  // still shows nothing until it resolves (same as before), but a spurious
  // reseed is now a silent no-op instead of a visible flash-to-blank.
  useEffect(() => {
    if (readOnly) return;
    const cachedRayBefore = getCachedRaytrace(raytraceSigForSide('before'));
    const cachedRayAfter = getCachedRaytrace(raytraceSigForSide('after'));
    const cachedVolBefore = getCachedVolumetric(volumetricSigForSide('before'));
    const cachedVolAfter = getCachedVolumetric(volumetricSigForSide('after'));
    const cachedVelocityBefore = getCachedVelocity(velocitySigForSide('before'));
    const cachedVelocityAfter = getCachedVelocity(velocitySigForSide('after'));
    const cachedModalBefore = getCachedModal(modalSigForSide('before'));
    const cachedModalAfter = getCachedModal(modalSigForSide('after'));
    // Bail out to the SAME `prev` reference when neither slot actually
    // changes value — the unconditional `{ before, after }` rebuild this
    // used to do created a brand-new wrapper object every time this effect
    // ran, even on a total no-op (both sides falling through to `prev.X`
    // unchanged). The markVizChanged effect above keys off these payload
    // references specifically so that kind of churn can't fan out into
    // spurious "there's a fresher viz cube" notifications.
    setRaytracePair((prev) => {
      const before = cachedRayBefore ?? prev.before;
      const after = cachedRayAfter ?? prev.after;
      return before === prev.before && after === prev.after ? prev : { before, after };
    });
    setVolumePair((prev) => {
      const before = cachedVolBefore ?? prev.before;
      const after = cachedVolAfter ?? prev.after;
      return before === prev.before && after === prev.after ? prev : { before, after };
    });
    setVelocityPair((prev) => {
      const before = cachedVelocityBefore ?? prev.before;
      const after = cachedVelocityAfter ?? prev.after;
      return before === prev.before && after === prev.after ? prev : { before, after };
    });
    setModalPair((prev) => {
      const before = cachedModalBefore ?? prev.before;
      const after = cachedModalAfter ?? prev.after;
      return before === prev.before && after === prev.after ? prev : { before, after };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizTrigger, voxelArmedSig]);

  // Modal — before/after pair, mirroring the raytrace/volumetric pattern for
  // round-trip symmetry (see persistedViz.ts). There's no live UI toggle for
  // this yet (VizToggle only offers Reflections/Sound pressure), so in
  // practice this only ever fetches the `after` (real-panels) side — but
  // keying the request on `compare` and caching per-side means a future
  // before/after modal UI needs no data-flow change, and an untreated-room
  // request already round-trips correctly today via panels: [].
  useEffect(() => {
    if (shouldHoldVizFetch({
      readOnly, neverFetch, hasPersisted: Boolean(persisted?.modal?.[compare]), vizGateOk,
    })) return;
    if (vizMode !== 'modal') return;
    const request = buildModalRequest(compare);
    const sig = modalSig(request, pipeline);
    const cached = getCachedModal(sig);
    if (cached) {
      setModalPair((prev) => (
        prev[compare] === cached ? prev : { ...prev, [compare]: cached }
      ));
      dispatch({
        type: 'SET_VIZ_STATUS',
        status: VIZ_STATUS.modalReady(
          cached.modes_used, cached.speakers_used, cached.speakers_total, modalView,
        ),
      });
      return;
    }
    if (voxelFetchBlocked()) {
      dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.simulationPending });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.computingModalField });
        // Voxel: wait on the SAME in-flight pre-warm the furniture step's
        // "Set up speakers" click started.  Dedupes by geometry signature so
        // we never submit a second modal_warm for the armed scene — the sync
        // modal call below hits the bare cache instead of triggering an
        // eigensolve on the HTTP request thread.
        if (pipeline === 'voxel') {
          await preWarmEigensolve(docRef.current, pipeline);
          if (cancelled) return;
        }
        // Topdown view asks for just the floor; walls view sends `null` so
        // the backend returns every wall facet it has (4 bounding-box walls
        // plus any wing walls / ceiling segments — only known after the
        // room is voxelised).  The renderer keys by `Object.entries`, so
        // extra entries fold in without a contract change.
        const resp = await fetchModal(
          sig,
          () => adminApi.modal(request, pipeline),
        );
        if (cancelled) return;
        setModalPair((prev) => ({ ...prev, [compare]: resp }));
        dispatch({
          type: 'SET_VIZ_STATUS',
          status: VIZ_STATUS.modalReady(
            resp.modes_used, resp.speakers_used, resp.speakers_total, modalView,
          ),
        });
      } catch (err) {
        if (!cancelled) {
          trackVizFailed('modal');
          dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.failed(err, 'modal') });
        }
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // buildModalScenePayload (= buildVolumetricPayload) intentionally elided
    // — it's a fresh function reference every render; including it would
    // retrigger this effect every render instead of only on a real trigger,
    // same as the volumetric/raytrace effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizMode, modalFreq, modalView, pipeline, vizTrigger, vizGateOk, voxelArmedSig, compare, dispatch, readOnly, neverFetch, persisted, skipVoxelGate]);

  // Volumetric — one sweep fetch per (geometry, side), no per-frequency
  // refetches.  The slider scrubs in real time off the preloaded GPU
  // texture (see VolumetricPressure.tsx), so the only network work is at
  // first entry into volumetric mode and the opportunistic warm of the
  // inactive before/after slot.
  //
  // Gate is intentionally NOT on `vizMode === 'volumetric'`.  The
  // particle cloud paints at low intensity in every viz mode (ambient
  // "the room is alive" pass), so we kick off the sweep whenever voxel
  // is armed and let the consumer decide how strongly to render it.
  useEffect(() => {
    if (shouldHoldVizFetch({
      readOnly, neverFetch, hasPersisted: Boolean(persisted?.volume), vizGateOk,
    })) return;

    const broadcastingStatus = vizMode === 'volumetric';
    const broadcast = (r: ModalVolumeSweepResponse) => {
      if (!broadcastingStatus) return;
      // The "N modes · NxNxN voxels · N freqs" telemetry was developer-facing
      // noise in the viewport — clear it once the field lands.  Genuine solver
      // warnings still surface; everything else resolves to no chip.
      dispatch({
        type: 'SET_VIZ_STATUS',
        status: r.warnings.length ? VIZ_STATUS.volumeWarnings(r.warnings.length) : VIZ_STATUS.none,
      });
    };

    // Module-cache hit or state hit (e.g. this candidate's field was warmed while it was
    // shown in the gallery, or the advanced editor is re-opening a scene
    // with a persisted volume cube): paint immediately, no network.
    // Checked against the cache directly rather than the `volumePair`
    // closure so a token bump that re-seeds state and re-runs this effect
    // in the same pass doesn't race into a redundant fetch.
    const sig = volumetricSigForSide(compare);
    const cached = getCachedVolumetric(sig);
    const currentPair = volumePair[compare];
    if (cached) {
      if (currentPair !== cached) {
        // Mirror the external cache hit into local state for render. Same
        // external-store-sync exception as the resync effect above.
        setVolumePair((prev) => (prev[compare] === cached ? prev : { ...prev, [compare]: cached }));
      }
      broadcast(cached);
      warmOtherVolume(compare);
      return;
    }
    // Gallery (skipVoxelGate): currentPair may be the previous candidate's
    // cube — do not treat it as a hit or Next never fetches B. Elsewhere
    // (persisted reopen, guided) the pair IS the current scene.
    if (currentPair && !skipVoxelGate) {
      broadcast(currentPair);
      warmOtherVolume(compare);
      return;
    }

    if (voxelFetchBlocked()) {
      if (vizMode === 'volumetric') {
        dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.simulationPending });
      }
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        if (broadcastingStatus) {
          const tag = docRef.current.panels.length > 0 ? ` (${compare})` : '';
          dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.preloadingField(tag) });
        }
        const payload = buildVolumetricPayload(compare);
        // df=2 Hz → 141 samples across the slider band.  Backend interp on
        // the GPU between adjacent samples covers the 1 Hz slider step;
        // 141 layers also stays inside WebGL2's minimum-spec array texture
        // limit so the cube fits everywhere.  fetchVolumetric stores the
        // result under `sig` and dedupes concurrent requests.
        const resp = await fetchVolumetric(sig, async () => {
          if (pipeline === 'voxel') await preWarmEigensolve(docRef.current, pipeline);
          return adminApi.modalVolumeSweep({ ...payload, ...VOL_SWEEP }, pipeline);
        });
        if (cancelled) return;
        setVolumePair((prev) => ({ ...prev, [compare]: resp }));
        broadcast(resp);
        warmOtherVolume(compare);
      } catch (err) {
        if (!cancelled && broadcastingStatus) {
          trackVizFailed('volume');
          dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.failed(err, 'volume') });
        }
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // volumePair intentionally elided from deps — including it would
    // retrigger this effect on every cache write, defeating the gate.
    // modalFreq deliberately NOT a dependency: the sweep cube already
    // covers every frequency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizMode, pipeline, vizTrigger, vizGateOk, voxelArmedSig, compare, dispatch, readOnly, neverFetch, persisted, skipVoxelGate]);

  // Particle velocity — deliberately the SIMPLEST of the four fetches.
  //
  // Unlike the pressure cloud it is not an ambient layer: it paints only when
  // the user has actually selected it, so the gate really is `vizMode ===
  // 'velocity'` (the volumetric effect's comment explains why that same gate
  // would be wrong there). No before/after warm either — nothing crossfades
  // it, so warming the inactive side would be a solve nobody looks at.
  useEffect(() => {
    if (readOnly || vizMode !== 'velocity') return;
    const request = buildVelocityRequest(compare);
    const sig = velocitySig(request, pipeline);
    const cached = getCachedVelocity(sig);
    const currentPair = velocityPair[compare];
    if (cached) {
      if (currentPair !== cached) {
        setVelocityPair((prev) => (
          prev[compare] === cached ? prev : { ...prev, [compare]: cached }
        ));
      }
      dispatch({
        type: 'SET_VIZ_STATUS',
        status: cached.warnings.length
          ? VIZ_STATUS.volumeWarnings(cached.warnings.length)
          : VIZ_STATUS.none,
      });
      return;
    }
    if (currentPair && !skipVoxelGate) {
      dispatch({
        type: 'SET_VIZ_STATUS',
        status: currentPair.warnings.length
          ? VIZ_STATUS.volumeWarnings(currentPair.warnings.length)
          : VIZ_STATUS.none,
      });
      return;
    }
    if (voxelFetchBlocked()) {
      dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.simulationPending });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.preloadingField('') });
        if (pipeline === 'voxel') await preWarmEigensolve(docRef.current, pipeline);
        const resp = await fetchVelocity(
          sig,
          () => adminApi.modalVelocity(request, pipeline),
        );
        if (cancelled) return;
        setVelocityPair((prev) => ({ ...prev, [compare]: resp }));
        dispatch({
          type: 'SET_VIZ_STATUS',
          status: resp.warnings.length
            ? VIZ_STATUS.volumeWarnings(resp.warnings.length)
            : VIZ_STATUS.none,
        });
      } catch (err) {
        if (!cancelled) {
          trackVizFailed('volume');
          dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.failed(err, 'volume') });
        }
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
    // velocityPair elided for the same reason volumePair is above: including
    // it would retrigger on every cache write and defeat the gate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizMode, pipeline, vizTrigger, vizGateOk, voxelArmedSig, compare, dispatch, readOnly, neverFetch, skipVoxelGate]);

  // Raytrace — same dual-fetch pattern as volumetric. Also feeds the
  // Reflections vizMode (point-cloud/heatmap toggle over a time/order-
  // filtered subset of this same trace) — reuse this fetch rather than
  // adding a second one for the same data.
  useEffect(() => {
    if (shouldHoldVizFetch({
      readOnly, neverFetch, hasPersisted: Boolean(persisted?.raytrace), vizGateOk,
    })) return;
    if (vizMode !== 'raytrace' && vizMode !== 'reflections') return;
    // Hide the before/after qualifier on an untreated scene — without
    // panels both sides resolve to the same bare-room trace.
    const compareSuffix = docRef.current.panels.length > 0 ? ` · ${compare}` : '';
    const compareTag = docRef.current.panels.length > 0 ? ` (${compare})` : '';
    const cached = getCachedRaytrace(raytraceSigForSide(compare));
    const currentPair = raytracePair[compare];
    if (cached) {
      if (currentPair !== cached) {
        setRaytracePair((prev) => (
          prev[compare] === cached ? prev : { ...prev, [compare]: cached }
        ));
      }
      dispatch({
        type: 'SET_VIZ_STATUS',
        status: VIZ_STATUS.rayPoints(cached.n_points, compareSuffix),
      });
      warmOtherRaytrace(compare);
      return;
    }
    if (currentPair && !skipVoxelGate) {
      dispatch({
        type: 'SET_VIZ_STATUS',
        status: VIZ_STATUS.rayPoints(currentPair.n_points, compareSuffix),
      });
      warmOtherRaytrace(compare);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.tracingRays(compareTag) });
        const req = buildRaytraceRequest(compare);
        // Read-through the module cache so a candidate the user already
        // visited returns its trace instantly instead of re-tracing, and
        // concurrent re-entries share one request.
        const resp = await fetchRaytrace(raytraceSig(req), () => adminApi.raytrace(req));
        if (cancelled) return;
        setRaytracePair((prev) => ({ ...prev, [compare]: resp }));
        dispatch({
          type: 'SET_VIZ_STATUS',
          status: VIZ_STATUS.rayPoints(resp.n_points, compareSuffix),
        });
        warmOtherRaytrace(compare);
      } catch (err) {
        if (!cancelled) {
          trackVizFailed('raytrace');
          dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.failed(err, 'raytrace') });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizMode, vizTrigger, vizGateOk, compare, dispatch, readOnly, neverFetch, persisted, skipVoxelGate]);

  // Advanced: when the scene has been edited since the last Scan the field is
  // held stale (and cleared from the outputs below), so prompt a re-scan
  // rather than leaving the viewport silently empty. Live gallery (and
  // guided) must also CLEAR a leftover chip — generate's first
  // SYNC_TRANSFORM used to paint "Scene changed" on the frame before
  // liveViz flipped, and nothing overwrote it if the volumetric cache hit
  // with vizMode still 'none'.
  const vizStatus = state.view.vizStatus;
  useEffect(() => {
    if (readOnly) return;
    if (live) {
      if (vizStatus === VIZ_STATUS.sceneChanged) {
        dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.none });
      }
      return;
    }
    if (!vizGateOk) {
      dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.sceneChanged });
    } else if (vizStatus === VIZ_STATUS.sceneChanged) {
      // Compute/undo brought the analysis back in sync with the scene. With
      // no viz mode selected, none of the modal/raytrace/volume effects below
      // run to overwrite this chip with a real status (they're gated on
      // vizMode !== 'none') — so this is the only path that clears it. Must
      // not depend on which visualization is selected.
      dispatch({ type: 'SET_VIZ_STATUS', status: VIZ_STATUS.none });
    }
  }, [live, vizGateOk, vizStatus, dispatch, readOnly]);

  // ── helpers ──────────────────────────────────────────────────────

  /** Scene payload for the volumetric request — empties panels for the
   *  bare-room "before" comparison; otherwise sends what's in the doc. Also
   *  reused as-is for the modal request (same before/after override). */
  function buildVolumetricPayload(side: 'before' | 'after') {
    const payload = docToScenePayload(docRef.current);
    return side === 'before' ? { ...payload, panels: [] } : payload;
  }

  function buildModalRequest(side: 'before' | 'after') {
    const surfaces: SurfaceKey[] | null =
      modalView === 'topdown' ? ['floor'] : null;
    return { ...buildVolumetricPayload(side), f: modalFreq, surfaces };
  }

  function modalSigForSide(side: 'before' | 'after'): string {
    return modalSig(buildModalRequest(side), pipeline);
  }

  function buildVelocityRequest(side: 'before' | 'after') {
    return { ...buildVolumetricPayload(side), ...VELOCITY_BAND };
  }

  function velocitySigForSide(side: 'before' | 'after'): string {
    return velocitySig(buildVelocityRequest(side), pipeline);
  }

  /** Cache key for one side's volumetric cube under the current doc +
   *  pipeline.  Reads docRef so it stays correct inside effects that fire
   *  after a token bump (docRef is refreshed earlier in the same pass). */
  function volumetricSigForSide(side: 'before' | 'after'): string {
    return volumetricSig(buildVolumetricPayload(side), VOL_SWEEP, pipeline);
  }

  /** Raytrace request — same before/after override (panels = [] for the
   *  bare-room pass; default `docToRaytraceRequest` packs `doc.panels`). */
  function buildRaytraceRequest(side: 'before' | 'after') {
    const opts = side === 'before' ? { panels: [] } : undefined;
    return docToRaytraceRequest(docRef.current, opts);
  }

  /** Cache key for one side's ray trace under the current doc. */
  function raytraceSigForSide(side: 'before' | 'after'): string {
    return raytraceSig(buildRaytraceRequest(side));
  }

  /** Background-warm the inactive volumetric side, but only if the room
   *  has panels — without panels the two sides are identical and the
   *  warm just doubles bandwidth. */
  function warmOtherVolume(active: 'before' | 'after') {
    const other: 'before' | 'after' = active === 'before' ? 'after' : 'before';
    if (docRef.current.panels.length === 0) return;
    if (voxelFetchBlocked()) return;
    const sig = volumetricSigForSide(other);
    if (getCachedVolumetric(sig)) {
      // Already warm — make sure the local slot reflects it and stop.
      setVolumePair((prev) => (prev[other] ? prev : { ...prev, [other]: getCachedVolumetric(sig) }));
      return;
    }
    (async () => {
      try {
        const payload = buildVolumetricPayload(other);
        const resp = await fetchVolumetric(sig, async () => {
          if (pipeline === 'voxel') await preWarmEigensolve(docRef.current, pipeline);
          return adminApi.modalVolumeSweep({ ...payload, ...VOL_SWEEP }, pipeline);
        });
        setVolumePair((prev) => ({ ...prev, [other]: resp }));
      } catch {
        // Silent — this is opportunistic; if it fails we'll retry on the
        // user's actual toggle and surface the error then.
      }
    })();
  }

  function warmOtherRaytrace(active: 'before' | 'after') {
    const other: 'before' | 'after' = active === 'before' ? 'after' : 'before';
    if (raytracePair[other]) return;
    if (docRef.current.panels.length === 0) return;
    const req = buildRaytraceRequest(other);
    const sig = raytraceSig(req);
    const cached = getCachedRaytrace(sig);
    if (cached) {
      setRaytracePair((prev) => (prev[other] ? prev : { ...prev, [other]: cached }));
      return;
    }
    (async () => {
      try {
        const resp = await fetchRaytrace(sig, () => adminApi.raytrace(req));
        setRaytracePair((prev) => ({ ...prev, [other]: resp }));
      } catch {
        // Silent — see warmOtherVolume.
      }
    })();
  }

  // Expose a response only while its visualization is the active mode, so a
  // stale fetch never paints after the user switches away. Before/after
  // selection reads from `state.view.compare` so a single source of truth
  // drives both this hook and the result-page Compare toggle.
  //
  // EXCEPTION: volumeResp is exposed regardless of vizMode.  The particle
  // cloud paints at low intensity in every viz mode (ambient pass), so
  // SceneCanvas needs the data even when the user is on Solid / Modal /
  // Rays.  Intensity is driven by vizMode at the consumer.
  // Advanced: scene edit sets afterStale (and bumps token past scannedToken).
  // vizGateOk is analysisMatchesScene — both signals, not token equality
  // alone — so one undo of N edits cannot resurrect the last scan's field.
  // Guided skips that hold (see vizGateOk above).
  // Public share often has only the `after` cube — don't leave controls
  // and the legend gated on a missing `before` slot.
  useEffect(() => {
    if (!readOnly || !persisted?.volume) return;
    const { before, after } = persisted.volume;
    if (compare === 'before' && !before && after) {
      dispatch({ type: 'SET_COMPARE', compare: 'after' });
    } else if (compare === 'after' && !after && before) {
      dispatch({ type: 'SET_COMPARE', compare: 'before' });
    }
  }, [readOnly, persisted, compare, dispatch]);

  const showViz = readOnly || vizGateOk;
  return {
    modalResp: showViz && vizMode === 'modal' ? activePairSlot(modalPair, compare) : null,
    raytraceResp:
      showViz && (vizMode === 'raytrace' || vizMode === 'reflections')
        ? activePairSlot(raytracePair, compare)
        : null,
    volumeResp: showViz ? activePairSlot(volumePair, compare) : null,
    velocityResp:
      showViz && vizMode === 'velocity' ? activePairSlot(velocityPair, compare) : null,
  };
}
